/**
 * Decrypts the `config` field of v2 payment-gateway API responses.
 *
 * Ported byte for byte from rentmy_pos's src/utils/paymentGatewayCrypto.ts (same backend,
 * same PaymentGatewayCipher on the server): v2 gateway endpoints (`v2/payments/gateway`,
 * `v2/payments/gateways`) return `config` as AES-256-GCM ciphertext instead of plain JSON, so
 * gateway credentials (API keys/secrets) never cross the wire — or sit in localStorage — in the
 * clear. The v1 endpoints (`payments/gateway`, `payments/gateways`) are unaffected and keep
 * returning plain JSON, but this app always uses v2, so this module is mandatory, not optional.
 *
 * Must match PaymentGatewayCipher::deriveStoreKey() on the backend byte for byte — same hash,
 * same empty salt, same info string. Any deviation (casing, whitespace, number formatting)
 * silently derives a different key and decryption fails with an opaque OperationError.
 */

const HKDF_INFO_PREFIX = 'payment-gateway-config:store:';

// Wire format is base64(iv[12] + ciphertext + tag[16]). AES-GCM in Web Crypto wants ciphertext
// and tag as one blob, so only the IV gets split off.
const IV_LENGTH = 12;
const GCM_TAG_LENGTH = 16;

// The IV is random per encrypt, so the same config yields new ciphertext on every fetch and
// cache keys are never reused across fetches. A store has a handful of gateways, so this cap
// only evicts entries nothing reads any more.
const CONFIG_CACHE_LIMIT = 64;

const configCache = new Map<string, unknown>();
const storeKeyCache = new Map<string, Promise<CryptoKey>>();

function getSubtle(): SubtleCrypto | null {
  return typeof crypto !== 'undefined' && crypto.subtle ? crypto.subtle : null;
}

/** True when this runtime can decrypt at all (WebView + key configured). */
export function isDecryptionAvailable(): boolean {
  const key = import.meta.env.VITE_PAYMENT_GATEWAY_ENCRYPTION_KEY as string | undefined;
  return !!(key && getSubtle());
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(String(value).trim());
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * storeKey = HKDF-SHA256(base key, salt = "", info = prefix + store id).
 * Per-store derivation means a leaked derived key only ever exposes one store's credentials.
 */
function deriveStoreKey(storeId: string | number): Promise<CryptoKey> {
  const cacheKey = String(storeId);
  const cached = storeKeyCache.get(cacheKey);
  if (cached) return cached;

  const subtle = getSubtle();
  const derived = (async () => {
    if (!subtle) throw new Error('Web Crypto SubtleCrypto is unavailable in this runtime.');
    const rawKey = import.meta.env.VITE_PAYMENT_GATEWAY_ENCRYPTION_KEY as string | undefined;
    if (!rawKey) throw new Error('VITE_PAYMENT_GATEWAY_ENCRYPTION_KEY is not configured.');
    const baseKey = base64ToBytes(rawKey);
    if (baseKey.length !== 32) {
      throw new Error(
        `VITE_PAYMENT_GATEWAY_ENCRYPTION_KEY must decode to exactly 32 bytes (got ${baseKey.length}).`,
      );
    }
    // Cast: TS's DOM lib types Uint8Array's buffer as ArrayBufferLike, which isn't assignable to
    // the stricter BufferSource generic these overloads expect — a lib-typing mismatch, not an
    // actual runtime issue.
    const hkdfKey = await subtle.importKey('raw', baseKey as BufferSource, 'HKDF', false, [
      'deriveKey',
    ]);
    return subtle.deriveKey(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(0),
        info: new TextEncoder().encode(HKDF_INFO_PREFIX + cacheKey),
      } as HkdfParams,
      hkdfKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt'],
    );
  })();

  // Don't cache a rejected derivation — a bad key would otherwise poison every later attempt
  // with the same rejected promise.
  derived.catch(() => storeKeyCache.delete(cacheKey));
  storeKeyCache.set(cacheKey, derived);
  return derived;
}

async function decryptPayload(payload: string, storeId: string | number): Promise<unknown> {
  const subtle = getSubtle();
  if (!subtle) throw new Error('Web Crypto SubtleCrypto is unavailable in this runtime.');
  const bytes = base64ToBytes(payload);
  if (bytes.length <= IV_LENGTH + GCM_TAG_LENGTH) {
    throw new Error('Encrypted config is too short to contain an iv and a tag.');
  }
  const key = await deriveStoreKey(storeId);
  const plain = await subtle.decrypt(
    { name: 'AES-GCM', iv: bytes.slice(0, IV_LENGTH) },
    key,
    // ciphertext + tag, handed over together as AES-GCM expects
    bytes.slice(IV_LENGTH),
  );
  const text = new TextDecoder().decode(plain);
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function cacheConfig(payload: string, config: unknown) {
  if (configCache.size >= CONFIG_CACHE_LIMIT) {
    const oldest = configCache.keys().next();
    if (!oldest.done) configCache.delete(oldest.value);
  }
  configCache.set(payload, config);
}

async function decryptCached(
  payload: string,
  storeId: string | number,
  label?: string,
): Promise<unknown> {
  if (configCache.has(payload)) return configCache.get(payload);
  let config: unknown = null;
  try {
    config = await decryptPayload(payload, storeId);
  } catch (e) {
    // A wrong key, a wrong store id, or a truncated payload all land here. Returning null keeps
    // the rest of the response usable and lets the calling component fall through its own
    // `config?.x` guards.
    console.error(
      `Payment gateway config could not be decrypted${label ? ' for ' + label : ''}:`,
      e,
    );
    return null;
  }
  cacheConfig(payload, config);
  return config;
}

async function decryptNode(node: unknown, storeId: string | number): Promise<unknown> {
  if (Array.isArray(node)) {
    const out: unknown[] = [];
    for (const item of node) out.push(await decryptNode(item, storeId));
    return out;
  }
  if (!node || typeof node !== 'object') return node;

  const source = node as Record<string, unknown>;
  const clone: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    const value = source[key];
    if (key === 'config' && typeof value === 'string' && value.length > 0) {
      clone[key] = await decryptCached(
        value,
        storeId,
        (source.name ?? source.id) as string | undefined,
      );
    } else if (value && typeof value === 'object') {
      clone[key] = await decryptNode(value, storeId);
    } else {
      clone[key] = value;
    }
  }
  return clone;
}

/**
 * Walk a v2 gateway response and return a copy with every `config` string replaced by its
 * decrypted object. Handles all response shapes: a flat array, the grouped
 * { card, atrium, additional } object, and a single gateway object.
 *
 * A row whose config is missing, empty, or already an object is passed through untouched, so
 * this is safe to run over v1-shaped data too.
 *
 * There is no silent fallback: with the v2 endpoint mandatory, gateway `config` always arrives
 * as ciphertext, so if decryption isn't available (no key configured, or non-browser runtime)
 * or no store id was given to derive a key from, this throws rather than handing back unusable
 * ciphertext as if it were real settings.
 */
export async function decryptGatewayData<T>(data: T, storeId: string | number | null): Promise<T> {
  if (data === null || data === undefined) return data;
  if (!isDecryptionAvailable()) {
    throw new Error(
      'VITE_PAYMENT_GATEWAY_ENCRYPTION_KEY is not configured — cannot read payment gateway settings.',
    );
  }
  if (storeId === null || storeId === undefined || storeId === '') {
    throw new Error('No store id available to derive the payment gateway key from.');
  }
  return decryptNode(data, storeId) as Promise<T>;
}
